import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { Countries, TP_COLORS } from '../../constants';
import { useAppSelector } from '../../hooks';
import { isEmailValid } from '../../helper';
import { TPTextField } from '../../components/Inputs';
import {
  createTrialWithAccount,
  getTrialsState,
  ICreateTrialWithAccount,
} from '../../store/V2/TrialsSlice';
import { TTrialState } from '../V2/Tutor/components/Calendar';

type TState = {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  isPasswordVisible?: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === deviceTimeZone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const initialState: TState = {
  name: '',
  email: '',
  phoneNumber: '',
  password: '',
  isPasswordVisible: false,
};

interface SignUpModalProps {
  open: boolean;
  trialState: TTrialState;
  onClose: () => void;
  onLoginClick: () => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({
  open,
  trialState,
  onClose,
  onLoginClick,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const { status } = useAppSelector(getTrialsState);

  // State
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({
    name: { ...defaultErrorData },
    email: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
    password: { ...defaultErrorData },
  });

  const isMdOrMore = useMediaQuery(theme.breakpoints.up('md'));

  const isSubmitDisabled =
    !state.name.trim().length ||
    errors.name.visible ||
    !state.email.trim().length ||
    errors.email.visible ||
    !state.phoneNumber.trim().length ||
    errors.phoneNumber.visible ||
    !state.password.trim().length ||
    errors.password.visible ||
    status === 'loading';

  useEffect(() => {
    if (state.name) {
      let _nameError = { ...defaultErrorData };

      if (state.name.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        name: _nameError,
      }));
    }
  }, [state.name]);

  useEffect(() => {
    if (state.email) {
      let _emailError = { ...defaultErrorData };

      if (!isEmailValid(state.email)) {
        _emailError = {
          visible: true,
          message: 'Invalid Email.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        email: _emailError,
      }));
    }
  }, [state.email]);

  useEffect(() => {
    if (state.password) {
      let _passwordError = { ...defaultErrorData };

      if (state.password.length < 8) {
        _passwordError = {
          visible: true,
          message: 'Password should have atleast 8 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        password: _passwordError,
      }));
    }
  }, [state.password]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _phoneNumberError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _phoneNumberError = {
          visible: true,
          message: 'Phone number should have atleast 8 characters.',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _phoneNumberError,
      }));
    }
  }, [state.phoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhoneNumberChange = (value: string) => {
    setState((prevState) => ({
      ...prevState,
      phoneNumber: value || '',
    }));
  };

  const togglePasswordVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      isPasswordVisible: !prevState.isPasswordVisible,
    }));
  };

  const handleSubmit = () => {
    if (
      !isSubmitDisabled &&
      trialState.selectedCurriculum &&
      trialState.selectedSubject &&
      trialState.selectedGrade &&
      trialState.selectedSlot
    ) {
      const apiData: ICreateTrialWithAccount = {
        name: state.name,
        email: state.email,
        phoneNumber: state.phoneNumber,
        password: state.password,
        timezone: deviceTimeZone,
        clientId: process.env.REACT_APP_TUTOPIYA_CLIENT_ID || '',
        clientSecret: process.env.REACT_APP_TUTOPIYA_CLIENT_SECRET || '',
        curriculum: trialState.selectedCurriculum.value,
        subject: trialState.selectedSubject.value,
        grade: trialState.selectedGrade.value,
        startDate: trialState.selectedSlot.start,
        endDate: trialState.selectedSlot.end,
        tutorId: trialState.selectedSlot.user || '',
      };

      dispatch(createTrialWithAccount(apiData));
    }
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '30px',
          borderRadius: '20px',
        },
      }}
    >
      <Grid container spacing="10px" alignItems="center">
        {isMdOrMore ? (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <div style={{ marginBottom: '16px', marginTop: '20px' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#006C9C"
                lineHeight="48.41px"
                fontSize="40px"
              >
                Your
                <span style={{ color: '#01A79F' }}> Learning Adventure</span>
                <br />
                Begins Here!
              </Typography>
            </div>
            <div>
              <Typography
                fontWeight={500}
                fontSize="16px"
                lineHeight="19.36px"
                color="#334155"
                maxWidth="464px"
              >
                Fill in your details to create an account, secure your free
                trial, and get class updates. Let’s make learning exciting for
                you!
              </Typography>
            </div>
            <Stack direction="row" justifyContent="flex-end" marginTop="-140px">
              <img
                src={`${process.env.PUBLIC_URL}/images/sign-up.png`}
                alt="tp-logo"
                style={{ width: '100%', maxWidth: '595px' }}
              />
            </Stack>
          </Grid>
        ) : null}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <div
            style={{
              maxWidth: '400px',
              margin: '0 auto',
            }}
          >
            <div style={{ marginBottom: '50px' }}>
              <Typography
                variant="h6"
                fontWeight={700}
                fontSize="20px"
                lineHeight="24.2px"
                textAlign="center"
                color={TP_COLORS.black}
              >
                You&apos;re Just One Click Away
              </Typography>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      name="name"
                      label="Full Name *"
                      value={state.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      error={errors.name.visible}
                      helperText={errors.name.message}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      name="email"
                      label="Email Address *"
                      value={state.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      error={errors.email.visible}
                      helperText={errors.email.message}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <PhoneInput
                    international
                    defaultCountry={defaultCountryCode}
                    placeholder="Enter phone number"
                    name="phoneNumber"
                    value={state.phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className="phone-number-input-custom-styles"
                    style={{
                      height: '52px',
                      backgroundColor: '#F1F5F9',
                    }}
                    numberInputProps={{
                      style: {
                        height: '52px',
                        borderColor: errors.phoneNumber.visible
                          ? '#FF2B2D'
                          : '#CBD5E1',
                        fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.4375em',
                      },
                    }}
                  />
                  {errors.phoneNumber.message ? (
                    <FormHelperText error style={{ marginLeft: '14px' }}>
                      {errors.phoneNumber.message}
                    </FormHelperText>
                  ) : null}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      type={state.isPasswordVisible ? 'text' : 'password'}
                      name="password"
                      label="Password *"
                      value={state.password}
                      onChange={handleInputChange}
                      placeholder="Password"
                      error={errors.password.visible}
                      helperText={errors.password.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePasswordVisibility}>
                              {state.isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    fullWidth
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitDisabled}
                    loading={status === 'loading'}
                  >
                    Book now
                  </LoadingButton>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    fontWeight={500}
                    color="#647488"
                    fontSize="14px"
                    lineHeight="16.94px"
                    textAlign="center"
                  >
                    Already have an account ?{' '}
                    <Button
                      variant="text"
                      onClick={() => {
                        onLoginClick();
                        onClose();
                      }}
                      style={{
                        textDecoration: 'underline',
                      }}
                    >
                      Login here
                    </Button>
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </div>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default SignUpModal;
